import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic } from 'lucide-react';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Card } from '../components/atoms/Card/Card';
import { Badge } from '../components/atoms/Badge/Badge';
import { Button } from '../components/atoms/Button/Button';
import { Text } from '../components/atoms/Typography/Text';
import { Select } from '../components/atoms/Select/Select';
import { VoiceExamples } from '../components/capture/VoiceExamples';
import { EmptyState } from '../components/atoms/EmptyState';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { OverflowMenu } from '../components/atoms/OverflowMenu';
import { Alert } from '../components/atoms/Alert/Alert';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { ConfirmDialog } from '../components/ui/ConfirmDialog/ConfirmDialog';
import { AiFeedbackControl } from '../components/aiFeedback/AiFeedbackControl';
import { CaptureReviewCard, captureCardHeadingId } from '../components/capture/CaptureReviewCard';
import { formatDateTime, openSuggestions } from '../components/capture/suggestionFormat';
import { useOptionalCapture } from '../components/capture/CaptureContext';
import { RecordNoteButton } from '../components/capture/RecordNoteButton';
import { OfflineCaptureQueue } from '../components/capture/OfflineCaptureQueue';
import { VoiceConsentOffer } from '../components/capture/VoiceConsentOffer';
import { useCaptureList, useCaptureMutations } from '../hooks/useCaptures';
import { ActiveAquariumContext } from '../contexts/AquariumContext';
import { useTranslation } from '../i18n/useTranslation';
import type { CaptureDto } from '../types/capture';
import './CaptureReviewPage.css';

const PROCESSING: ReadonlySet<CaptureDto['status']> = new Set(['queued', 'transcribing', 'transcribed', 'analyzing']);

function isOpen(capture: CaptureDto): boolean {
  return capture.status === 'review' && openSuggestions(capture.suggestions).length > 0;
}

function isRecentlySaved(capture: CaptureDto, now: number): boolean {
  return capture.savedCount > 0 && capture.undoableUntil !== null && new Date(capture.undoableUntil).getTime() > now;
}

/**
 * "To review" (/review): one inbox for the captures of all of the user's
 * tanks. Nothing is saved before the user saves a card; saved cards stay
 * undoable for 24 h under "Recently saved".
 */
export function CaptureReviewPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const navigate = useNavigate();
  const capture = useOptionalCapture();
  const activeAquariumId = useContext(ActiveAquariumContext)?.activeAquariumId ?? null;
  const { data, isLoading, isError, refetch, isRefetching } = useCaptureList();
  const { retry, discard, undo } = useCaptureMutations();
  const [tankFilter, setTankFilter] = useState('');
  const [undoTarget, setUndoTarget] = useState<CaptureDto | null>(null);
  const [discardTarget, setDiscardTarget] = useState<CaptureDto | null>(null);
  const [notice, setNotice] = useState<{ tone: 'danger' | 'success'; text: string } | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [pendingFocus, setPendingFocus] = useState<{ after: string } | null>(null);
  const emptyRef = useRef<HTMLDivElement>(null);

  const captures = useMemo(() => data ?? [], [data]);
  const tanks = useMemo(() => {
    const byId = new Map<string, string>();
    for (const c of captures) byId.set(c.aquariumId, c.aquariumName);
    return [...byId.entries()].map(([id, name]) => ({ id, name }));
  }, [captures]);
  const visible = tankFilter ? captures.filter((c) => c.aquariumId === tankFilter) : captures;
  const now = Date.now();
  const processing = visible.filter((c) => PROCESSING.has(c.status));
  const failed = visible.filter((c) => c.status === 'failed');
  const open = visible.filter(isOpen);
  const recent = visible.filter((c) => isRecentlySaved(c, now));

  // After a save the focus moves on to the next card, or to the empty state (WCAG 2.4.3).
  useEffect(() => {
    if (!pendingFocus) return;
    const ids = open.map((c) => c.id);
    // Wait until the refreshed list no longer holds the saved card.
    if (ids.includes(pendingFocus.after)) return;
    const target = ids[0];
    if (target) document.getElementById(captureCardHeadingId(target))?.focus();
    else emptyRef.current?.focus();
    setPendingFocus(null);
  }, [open, pendingFocus]);

  const timeOf = (c: CaptureDto): string => formatDateTime(c.recordedAt, language);

  const startOrWrite = (): void => {
    if (capture?.available) {
      capture.startRecording();
      return;
    }
    navigate(activeAquariumId ? `/aquariums/${encodeURIComponent(activeAquariumId)}/journal` : '/aquariums');
  };

  let content;
  if (isLoading) {
    content = (
      <Section title={t('voice.review.section.open')}>
        <Card>
          <LiveRegion busy data-testid="capture-review-loading">
            <Text color="muted">{t('voice.review.loading')}</Text>
          </LiveRegion>
        </Card>
      </Section>
    );
  } else if (isError) {
    content = (
      <Section title={t('voice.review.section.open')}>
        <LoadErrorState
          data-testid="capture-review-error"
          title={t('voice.review.error.title')}
          message={t('voice.review.error.body')}
          retryLabel={t('voice.review.error.retry')}
          retrying={isRefetching}
          onRetry={() => void refetch()}
        />
      </Section>
    );
  } else {
    content = (
      <>
        {tanks.length >= 2 && (
          <div className="capture-review-page__filters">
            <Select
              label={t('voice.review.filterTank')}
              value={tankFilter}
              onChange={(e) => setTankFilter(e.target.value)}
              options={[{ value: '', label: t('voice.review.filterAllTanks') }, ...tanks.map((tank) => ({ value: tank.id, label: tank.name }))]}
            />
          </div>
        )}

        <OfflineCaptureQueue />

        {notice && (
          <Alert tone={notice.tone} compact onDismiss={() => setNotice(null)} dismissLabel={t('common.close')}>
            {notice.text}
          </Alert>
        )}

        {(processing.length > 0 || failed.length > 0) && (
          <Section title={t('voice.review.section.processing')} data-testid="capture-review-processing">
            <ul className="capture-review-page__list">
              {processing.map((c) => {
                const waiting = c.status === 'transcribed' && c.waitingForCredits;
                const badge = waiting
                  ? t('voice.review.status.waitingForCredits')
                  : c.status === 'queued'
                    ? t('voice.review.status.queued')
                    : c.status === 'transcribing'
                      ? t('voice.review.status.transcribing')
                      : t('voice.review.status.analyzing');
                return (
                  <li key={c.id}>
                    <Card variant="outlined" padding="md" className="capture-review-page__status-card">
                      <Badge tone={waiting ? 'warning' : 'info'}>{badge}</Badge>
                      <Text>{waiting ? t('voice.review.waitingItem', { time: timeOf(c) }) : t('voice.review.processingItem', { time: timeOf(c) })}</Text>
                    </Card>
                  </li>
                );
              })}
              {failed.map((c) => (
                <li key={c.id}>
                  <Card variant="outlined" padding="md" className="capture-review-page__status-card" data-testid={`capture-failed-${c.id}`}>
                    <div className="capture-review-page__status-text">
                      <Badge tone="warning">{t('voice.review.status.failed')}</Badge>
                      <Text>{t('voice.review.failedItem', { time: timeOf(c) })}</Text>
                      {c.errorCode && <Text variant="caption" color="muted">{t(`voice.review.failedReason.${c.errorCode}`)}</Text>}
                    </div>
                    <div className="capture-review-page__status-actions">
                      <Button
                        variant="secondary"
                        size="sm"
                        isLoading={retry.isPending && retry.variables === c.id}
                        onClick={() =>
                          retry.mutate(c.id, { onError: () => setNotice({ tone: 'danger', text: t('voice.review.retryFailed') }) })
                        }
                      >
                        {t('voice.review.retryProcessing')}
                      </Button>
                      <OverflowMenu
                        label={t('voice.review.failedMore')}
                        items={[{ id: 'discard', label: t('voice.review.discardCapture'), tone: 'danger', onSelect: () => setDiscardTarget(c) }]}
                      />
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
            <LiveRegion visuallyHidden>
              {processing.length > 0 ? t('voice.review.announceProcessing', { count: processing.length }) : ''}
            </LiveRegion>
          </Section>
        )}

        <Section title={t('voice.review.section.open')}>
          {open.length === 0 ? (
            <div ref={emptyRef} tabIndex={-1} className="capture-review-page__empty" data-testid="capture-review-empty">
              <EmptyState
                icon={<Mic size={32} />}
                headline={t('voice.review.empty.title')}
                description={t('voice.review.empty.body')}
                actionLabel={capture?.available ? t('voice.review.empty.record') : t('voice.review.empty.write')}
                onAction={startOrWrite}
                headingLevel={3}
              />
              {capture?.available && <VoiceExamples className="capture-review-page__examples" />}
            </div>
          ) : (
            <ul className="capture-review-page__cards">
              {open.map((c) => (
                <li key={c.id}>
                  <CaptureReviewCard
                    capture={c}
                    onSaved={(id, remaining) => {
                      setAnnouncement(t('voice.review.announceSaved', { count: open.length - (remaining > 0 ? 0 : 1) }));
                      if (remaining === 0) setPendingFocus({ after: id });
                    }}
                    onDiscarded={(id) => setPendingFocus({ after: id })}
                  />
                </li>
              ))}
            </ul>
          )}
          <LiveRegion visuallyHidden>{announcement}</LiveRegion>
        </Section>

        {recent.length > 0 && (
          <Section title={t('voice.review.section.recent')} data-testid="capture-review-recent">
            <ul className="capture-review-page__recent">
              {recent.map((c) => (
                <li key={c.id} className="capture-review-page__recent-item">
                  <div className="capture-review-page__recent-row">
                    <Text>{t('voice.review.recentItem', { time: timeOf(c), count: c.savedCount, tank: c.aquariumName })}</Text>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('voice.review.undoLabel', { time: timeOf(c) })}
                      onClick={() => setUndoTarget(c)}
                    >
                      {t('voice.review.undo')}
                    </Button>
                  </div>
                  <AiFeedbackControl
                    outputType="voice_capture"
                    outputId={c.id}
                    aquariumId={c.aquariumId}
                    labels={{
                      prompt: t('voice.card.feedbackQuestion'),
                      up: t('voice.card.feedbackUp'),
                      down: t('voice.card.feedbackDown'),
                      commentLabel: t('voice.card.feedbackComment'),
                      submit: t('voice.card.feedbackSend'),
                    }}
                  />
                </li>
              ))}
            </ul>
          </Section>
        )}
      </>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default" data-testid="capture-review-page">
          <PageHeader
            title={t('voice.review.title')}
            description={t('voice.review.subtitle')}
            actions={<RecordNoteButton variant="primary" />}
          />
          <VoiceConsentOffer />
          {content}

          <ConfirmDialog
            isOpen={undoTarget !== null}
            title={t('voice.review.undoTitle')}
            message={t('voice.review.undoConfirm', { count: undoTarget?.savedCount ?? 0 })}
            confirmLabel={t('voice.review.undo')}
            cancelLabel={t('common.cancel')}
            isConfirming={undo.isPending}
            onCancel={() => setUndoTarget(null)}
            onConfirm={() => {
              if (!undoTarget) return;
              undo.mutate(undoTarget.id, {
                onSuccess: () => setNotice({ tone: 'success', text: t('voice.review.undone') }),
                onError: () => setNotice({ tone: 'danger', text: t('voice.review.undoFailed') }),
                onSettled: () => setUndoTarget(null),
              });
            }}
          />
          <ConfirmDialog
            isOpen={discardTarget !== null}
            title={t('voice.review.discardTitle')}
            message={t('voice.review.discardBody')}
            confirmLabel={t('voice.review.discardCapture')}
            cancelLabel={t('common.cancel')}
            isConfirming={discard.isPending}
            onCancel={() => setDiscardTarget(null)}
            onConfirm={() => {
              if (!discardTarget) return;
              discard.mutate(discardTarget.id, { onSettled: () => setDiscardTarget(null) });
            }}
          />
        </PageContent>
      }
    />
  );
}

export default CaptureReviewPage;
